import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { customViewTemplateType, repCodeType } from './types'
import axios from 'axios'

type userType = {
  email: string | null
  displayName: string | null
  role: {
    admin: boolean
    operations: boolean
    sales: boolean
  }
}

type dataContextType = {
  user: userType
  repCodes: repCodeType[]
  setRepCodes: React.Dispatch<React.SetStateAction<repCodeType[]>>
  customViewTemplates: customViewTemplateType[] | null
  customViewTemplatesLoading: boolean
  reloadCustomViewTemplates: () => Promise<void>
  selectedCustomViewTemplateName: string | null
  setSelectedCustomViewTemplateName: React.Dispatch<React.SetStateAction<string | null>>
  paymentTerms: { id: string; name: string }[]
  setPaymentTerms: React.Dispatch<React.SetStateAction<{ id: string; name: string }[]>>
}

const dataContext = createContext<dataContextType>({
  user: {
    email: null,
    displayName: null,
    role: {
      admin: false,
      operations: false,
      sales: false,
    },
  },
  repCodes: [],
  setRepCodes: (() => {
    return
  }) as React.Dispatch<React.SetStateAction<repCodeType[]>>,
  customViewTemplates: null,
  customViewTemplatesLoading: false,
  reloadCustomViewTemplates: () => Promise.resolve(),
  selectedCustomViewTemplateName: null,
  setSelectedCustomViewTemplateName: () => undefined,
  paymentTerms: [],
  setPaymentTerms: () => undefined,
})

export function DataProvider({
  email,
  displayName,
  children,
}: {
  email: string
  displayName: string | null
  children: React.ReactNode
}) {
  const [user, setUser] = useState<userType>({
    email: null,
    displayName: null,
    role: {
      // TODO: more roles
      admin: false,
      operations: false,
      sales: false,
    },
  })
  const [repCodes, setRepCodes] = useState<repCodeType[]>([])
  const [customViewTemplates, setCustomViewTemplates] = useState<customViewTemplateType[] | null>(null)
  const [customViewTemplatesLoading, setCustomViewTemplatesLoading] = useState(false)
  const [selectedCustomViewTemplateName, setSelectedCustomViewTemplateName] = useState<string | null>(() =>
    localStorage.getItem('selectedCustomViewTemplate'),
  )
  const [paymentTerms, setPaymentTerms] = useState<{ id: string; name: string }[]>([])

  const reloadCustomViewTemplates = useCallback(async () => {
    if (!email) return
    setCustomViewTemplatesLoading(true)
    try {
      const response = await axios.get<customViewTemplateType[]>(`/customViewTemplate/${email}`)
      setCustomViewTemplates(response.data)
    } catch (err) {
      console.error(err)
      setCustomViewTemplates([])
    } finally {
      setCustomViewTemplatesLoading(false)
    }
  }, [email])

  useEffect(() => {
    if (!email) return

    setUser(prev => ({ ...prev, email, displayName }))
    setCustomViewTemplates(null)

    axios
      .get(`/userRole/${email}`)
      .then(res => {
        setUser(prev => ({
          ...prev,
          email,
          displayName,
          role: res.data,
        }))
      })
      .catch(err => {
        console.error(err)
        setUser({
          email,
          displayName,
          role: {
            admin: false,
            operations: false,
            sales: false,
          },
        })
      })
  }, [email, displayName])

  useEffect(() => {
    if (selectedCustomViewTemplateName) {
      localStorage.setItem('selectedCustomViewTemplate', selectedCustomViewTemplateName)
    } else {
      localStorage.removeItem('selectedCustomViewTemplate')
    }
  }, [selectedCustomViewTemplateName])

  return (
    <dataContext.Provider
      value={{
        user,
        repCodes,
        setRepCodes,
        customViewTemplates,
        customViewTemplatesLoading,
        reloadCustomViewTemplates,
        selectedCustomViewTemplateName,
        setSelectedCustomViewTemplateName,
        paymentTerms,
        setPaymentTerms,
      }}
    >
      {children}
    </dataContext.Provider>
  )
}

export function useUser() {
  const { user } = useContext(dataContext)
  return user
}

export function useRepCodes(): [repCodeType[], React.Dispatch<React.SetStateAction<repCodeType[]>>] {
  const { repCodes, setRepCodes } = useContext(dataContext)
  useEffect(() => {
    if (repCodes.length === 0) {
      axios
        .get('/repCode')
        .then(response => {
          setRepCodes(response.data)
        })
        .catch(err => {
          console.error(err)
        })
    }
  }, [])
  return [repCodes, setRepCodes]
}

export function useCustomViewTemplates() {
  const {
    customViewTemplates,
    customViewTemplatesLoading,
    reloadCustomViewTemplates,
    selectedCustomViewTemplateName,
    setSelectedCustomViewTemplateName,
    user,
  } = useContext(dataContext)

  useEffect(() => {
    if (!user.email || customViewTemplates !== null) return
    reloadCustomViewTemplates()
  }, [user.email, customViewTemplates, reloadCustomViewTemplates])

  return {
    templates: customViewTemplates ?? [],
    loading: customViewTemplates === null || customViewTemplatesLoading,
    reload: reloadCustomViewTemplates,
    selectedTemplateName: selectedCustomViewTemplateName,
    setSelectedTemplateName: setSelectedCustomViewTemplateName,
  }
}

export function usePaymentTerms() {
  const { paymentTerms, setPaymentTerms } = useContext(dataContext)
  useEffect(() => {
    if (paymentTerms.length === 0) {
      axios
        .get('/netsuite/terms')
        .then(response => {
          setPaymentTerms(response.data)
        })
        .catch(err => {
          console.error(err)
        })
    }
  }, [])
  return paymentTerms
}
